import { RiInstanceLine } from 'react-icons/ri';
import { CircleHelpIcon, HardDrive, TerminalIcon } from 'lucide-react';
import CopyButton from '@/components/ui/copy-to-clipboard/copy-button';
import {
  TooltipContent,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { fCurrencyUSDExtended } from '@/utils/formatNumber';
import { Separator } from '@/components/ui/separator';

interface PricingCardProps {
  instance_type: string;
  price: {
    on_demand: {
      hours: number;
      minutes: number;
      seconds: number;
    };
    spot: {
      hours: number;
      minutes: number;
      seconds: number;
    };
    storage: {
      month: number;
    };
  };
}

const PricingCard = ({ instance_type, price }: PricingCardProps) => {
  const createInstance = (instance_type: string) => {
    return `float16 project create --instance ${instance_type}`;
  };

  return (
    <div className="group w-full rounded-lg border p-4 shadow transition-all">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <RiInstanceLine className="" />
          <div className="text-base font-semibold">{instance_type}</div>
          {/* <CopyIconButton text={instance_type} /> */}
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <CopyButton
                slot={{
                  button: {
                    className:
                      'opacity-0 group-hover:opacity-100 transition-opacity',
                    size: 'sm',
                    variant: 'outline',
                  },
                }}
                text={createInstance(instance_type)}
                iconCopy={<TerminalIcon className="h-3 w-3" />}
                tooltip="Copy Create Instance command to clipboard"
                activeToast
              >
                Create
              </CopyButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Copy Create Instance command to clipboard</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="mt-4 flex flex-col gap-4">
        <div className="relative grid grid-cols-2 gap-6">
          <div>
            <h3 className="mb-2 flex items-center text-sm font-medium">
              <span className="mr-2 rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                On-Demand
              </span>
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm font-semibold">Per Second</span>
              </div>
              <span className="text-lg font-bold">
                {fCurrencyUSDExtended(price.on_demand.seconds)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-xs">Per Hourly</span>
              </div>
              <span className="">
                {fCurrencyUSDExtended(price.on_demand.hours)}
              </span>
            </div>
          </div>

          <div>
            <h3 className="mb-2 flex items-center gap-1 text-sm font-medium">
              <span className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                Spot
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CircleHelpIcon className="h-3 w-3 justify-self-end cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-xs">
                      Spot instances offer significant savings but may be
                      interrupted at any time until resources become available
                      again
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="text-xs text-success">
                {price.on_demand.seconds > 0 ? (
                  <>
                    Save{' '}
                    {Math.round(
                      (1 - price.spot.seconds / price.on_demand.seconds) * 100
                    )}
                    %
                  </>
                ) : null}
              </span>
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm font-semibold">Per Second</span>
              </div>
              <span className="text-lg font-bold">
                {fCurrencyUSDExtended(price.spot.seconds)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-xs">Per Hourly</span>
              </div>
              <span className="">{fCurrencyUSDExtended(price.spot.hours)}</span>
            </div>
          </div>
        </div>

        <Separator className="col-span-full" />
        <div className="col-span-full flex items-center justify-between">
          <div className="flex items-center">
            <HardDrive className="mr-2 h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Storage (GB/Month)</span>
          </div>
          <span>{fCurrencyUSDExtended(price.storage.month)}</span>
        </div>
      </div>
    </div>
  );
};

export default PricingCard;
